/**
 * WeatherPage Component
 *
 * Dedicated page for browsing and selecting weather files from the global library.
 * Selection syncs with the Simulation page via the file store.
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { WeatherFileLibrary } from '../weather';
import type { WeatherFileMetadata } from '../weather';
import { useFileStore } from '../storage';
import type { LibraryWeatherFile } from '../storage';

export function WeatherPage() {
  const {
    selectedLibraryWeatherFile,
    setSelectedWeatherFile,
    setSelectedLibraryWeatherFile,
  } = useFileStore();

  // Track recently used files for quick access
  const addToRecentFiles = useCallback((file: WeatherFileMetadata) => {
    const recentKey = 'weather-recent-files';
    const maxRecent = 10;
    try {
      const stored = localStorage.getItem(recentKey);
      const recent: WeatherFileMetadata[] = stored ? JSON.parse(stored) : [];
      // Remove if already exists (to move to front)
      const filtered = recent.filter((f) => f.id !== file.id);
      // Add to front
      const updated = [file, ...filtered].slice(0, maxRecent);
      localStorage.setItem(recentKey, JSON.stringify(updated));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Handle selection from the library
  const handleSelect = useCallback(
    (file: WeatherFileMetadata) => {
      // Convert WeatherFileMetadata to LibraryWeatherFile for persistence
      // Build climate zone object conditionally to satisfy exactOptionalPropertyTypes
      const climateZone: LibraryWeatherFile['climateZone'] = {};
      if (file.climateZone.ashrae) {
        climateZone.ashrae = file.climateZone.ashrae;
      }
      if (file.climateZone.koppen) {
        climateZone.koppen = file.climateZone.koppen;
      }

      const libraryFile: LibraryWeatherFile = {
        id: file.id,
        name: file.name,
        url: file.urls.epw,
        location: {
          city: file.location.city,
          region: file.location.region,
          country: file.location.country,
          countryCode: file.location.countryCode,
          latitude: file.location.latitude,
          longitude: file.location.longitude,
          elevation: file.location.elevation,
          timezone: file.location.timezone,
        },
        climateZone,
        sourceType: file.source.type,
      };

      // Track in recent files for quick access
      addToRecentFiles(file);

      // Store the library file in the file store for syncing with SimulationPage
      setSelectedLibraryWeatherFile(libraryFile);
      // Clear the bundled weather file selection
      setSelectedWeatherFile(null);
    },
    [setSelectedLibraryWeatherFile, setSelectedWeatherFile, addToRecentFiles]
  );

  // Handle clearing the selection
  const handleClearSelection = useCallback(() => {
    setSelectedLibraryWeatherFile(null);
  }, [setSelectedLibraryWeatherFile]);

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Page Header */}
      <header className="border-b bg-background px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold sm:text-2xl">Weather Files</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Browse 54,000+ weather files from climate.onebuilding.org
            </p>
          </div>
          {selectedLibraryWeatherFile && (
            <Link
              to="/simulate"
              className="hidden shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 sm:inline-flex"
            >
              Run Simulation
            </Link>
          )}
        </div>
      </header>

      {/* Selection banner - shown only when a library file is selected */}
      {selectedLibraryWeatherFile && (
        <div
          className="flex items-center gap-3 border-b bg-green-50 px-4 py-2 dark:bg-green-950/30"
          role="status"
          aria-live="polite"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-400"
            aria-hidden="true"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <div className="min-w-0 flex-1">
            <span className="font-medium">{selectedLibraryWeatherFile.name}</span>
            <span className="mx-2 text-muted-foreground">·</span>
            <span className="text-sm text-muted-foreground">
              {selectedLibraryWeatherFile.location.city},{' '}
              {selectedLibraryWeatherFile.location.country}
            </span>
          </div>
          <button
            onClick={handleClearSelection}
            className="shrink-0 rounded px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Clear weather file selection"
          >
            Clear
          </button>
          <Link
            to="/simulate"
            className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 sm:hidden"
          >
            Run Simulation
          </Link>
        </div>
      )}

      {/* Weather File Library */}
      <div className="min-h-0 flex-1">
        <WeatherFileLibrary
          onSelect={handleSelect}
          selectedFileId={selectedLibraryWeatherFile?.id}
        />
      </div>
    </div>
  );
}

export default WeatherPage;
